import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {TranslateLoader, TranslationObject} from '@ngx-translate/core';
import {catchError, forkJoin, map, Observable, of} from 'rxjs';
import {FirebaseService} from '../../../services/firebase/firebase.service';

@Injectable({providedIn: 'root'})
export class BackendTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);
  public static readonly I18N_API_PATH = '/api/i18n/';
  private static readonly PLATFORM_TYPE = 'MOBILE';
  private static readonly LOCAL_I18N_PREFIX = './../assets/i18n/';

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin({
      local: this.getLocalTranslations(lang),
      backend: this.getBackendTranslations(lang)
    }).pipe(
      map(({local, backend}) => this.mergeTranslations(local, backend))
    );
  }

  private getLocalTranslations(lang: string): Observable<TranslationObject> {
    return this.http
      .get<TranslationObject>(`${BackendTranslateLoader.LOCAL_I18N_PREFIX}${lang}.json`)
      .pipe(catchError(() => of<TranslationObject>({})));
  }

  private getBackendTranslations(lang: string): Observable<Record<string, string>> {
    const url = `${FirebaseService.getApiBasePath()}${BackendTranslateLoader.I18N_API_PATH}${lang}`;
    return this.http
      .get<Record<string, string>>(url, {params: {platformType: BackendTranslateLoader.PLATFORM_TYPE}})
      .pipe(catchError(() => of<Record<string, string>>({})));
  }

  private mergeTranslations(local: TranslationObject, backend: Record<string, string>): TranslationObject {
    const merged: TranslationObject = {...local};
    Object.entries(backend).forEach(([key, value]) => this.setNestedValue(merged, key, value));
    return merged;
  }

  private setNestedValue(target: TranslationObject, path: string, value: string): void {
    const keys = path.split('.');
    let current: TranslationObject = target;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        if (typeof current[key] !== 'object' || current[key] === null) {
          current[key] = {};
        }
        current = current[key] as TranslationObject;
      }
    });
  }
}
